import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {
  Recognition,
  RecognitionType,
  FeedView,
  LeaderboardEntry,
  BadgeItem,
  Reward,
  RecognitionStats,
  RecognitionAnalytics,
  CompanyValue,
  Comment,
  TrendingValue,
  TopRecognizedEmployee,
} from '../types/recognition'

interface RecognitionState {
  // Feed
  recognitions: Recognition[]
  feedLoading: boolean
  feedPage: number
  feedTotalPages: number
  feedFilter: RecognitionType | 'all'
  feedView: FeedView

  // Leaderboard
  leaderboard: LeaderboardEntry[]
  leaderboardLoading: boolean
  leaderboardTab: 'received' | 'teams'
  leaderboardPeriod: 'week' | 'month' | 'quarter' | 'year'
  leaderboardTotal: number
  leaderboardPage: number
  leaderboardPageSize: number

  // Badges
  badges: BadgeItem[]
  badgesLoading: boolean
  badgeCategory: string

  // Rewards
  rewards: Reward[]
  rewardsLoading: boolean
  rewardCategory: string

  // Stats & Dashboard
  myStats: RecognitionStats | null
  statsLoading: boolean
  trendingValues: TrendingValue[]
  topRecognized: TopRecognizedEmployee[]
  dashboardLoading: boolean

  // Analytics
  analytics: RecognitionAnalytics | null
  analyticsLoading: boolean

  // Company Values
  companyValues: CompanyValue[]

  // Give Recognition Dialog
  isGiveDialogOpen: boolean

  // Feed refresh trigger — increment to refetch feed
  feedVersion: number

  // Highlighted card (from notification deep-link)
  highlightedRecognitionId: string | null

  // Error
  error: string | null
}

interface RecognitionActions {
  // Feed
  setRecognitions: (recognitions: Recognition[]) => void
  prependRecognition: (recognition: Recognition) => void
  setFeedLoading: (loading: boolean) => void
  setFeedFilter: (filter: RecognitionType | 'all') => void
  setFeedView: (view: FeedView) => void
  setFeedPage: (page: number) => void
  setFeedTotalPages: (total: number) => void
  updateRecognitionReaction: (id: string, reactionKey: keyof Recognition['reactions'], delta: number) => void
  addRecognitionComment: (id: string, comment: Comment) => void

  // Leaderboard
  setLeaderboard: (entries: LeaderboardEntry[], total?: number) => void
  setLeaderboardLoading: (loading: boolean) => void
  setLeaderboardTab: (tab: 'received' | 'teams') => void
  setLeaderboardPeriod: (period: 'week' | 'month' | 'quarter' | 'year') => void
  setLeaderboardPage: (page: number) => void
  setLeaderboardPageSize: (size: number) => void

  // Badges
  setBadges: (badges: BadgeItem[]) => void
  setBadgesLoading: (loading: boolean) => void
  setBadgeCategory: (category: string) => void

  // Rewards
  setRewards: (rewards: Reward[]) => void
  setRewardsLoading: (loading: boolean) => void
  setRewardCategory: (category: string) => void

  // Stats & Dashboard
  setMyStats: (stats: RecognitionStats) => void
  setStatsLoading: (loading: boolean) => void
  setTrendingValues: (values: TrendingValue[]) => void
  setTopRecognized: (employees: TopRecognizedEmployee[]) => void
  setDashboardLoading: (loading: boolean) => void

  // Analytics
  setAnalytics: (analytics: RecognitionAnalytics) => void
  setAnalyticsLoading: (loading: boolean) => void

  // Company Values
  setCompanyValues: (values: CompanyValue[]) => void

  // Feed refresh
  refreshFeed: () => void

  // Highlight
  setHighlightedRecognitionId: (id: string | null) => void

  // Dialog
  openGiveDialog: () => void
  closeGiveDialog: () => void

  // Error
  setError: (error: string | null) => void

  // Reset
  reset: () => void
}

type RecognitionStore = RecognitionState & RecognitionActions

const initialState: RecognitionState = {
  recognitions: [],
  feedLoading: true,
  feedPage: 1,
  feedTotalPages: 1,
  feedFilter: 'all',
  feedView: 'company' as FeedView,
  leaderboard: [],
  leaderboardLoading: true,
  leaderboardTab: 'received',
  leaderboardPeriod: 'month',
  leaderboardTotal: 0,
  leaderboardPage: 1,
  leaderboardPageSize: 20,
  badges: [],
  badgesLoading: true,
  badgeCategory: 'all',
  rewards: [],
  rewardsLoading: true,
  rewardCategory: 'all',
  myStats: null,
  statsLoading: true,
  trendingValues: [],
  topRecognized: [],
  dashboardLoading: true,
  analytics: null,
  analyticsLoading: true,
  companyValues: [],
  isGiveDialogOpen: false,
  feedVersion: 0,
  highlightedRecognitionId: null,
  error: null,
}

export const useRecognitionStore = create<RecognitionStore>()(
  devtools(
    (set) => ({
      ...initialState,

      // Feed
      setRecognitions: (recognitions) => set({ recognitions }),
      prependRecognition: (recognition) =>
        set((state) => ({
          recognitions: [recognition, ...state.recognitions],
        })),
      setFeedLoading: (loading) => set({ feedLoading: loading }),
      setFeedFilter: (filter) => set({ feedFilter: filter, feedPage: 1 }),
      setFeedView: (view) => set({ feedView: view, feedFilter: 'all', feedPage: 1 }),
      setFeedPage: (page) => set({ feedPage: page }),
      setFeedTotalPages: (total) => set({ feedTotalPages: total }),
      updateRecognitionReaction: (id, reactionKey, delta) =>
        set((state) => ({
          recognitions: state.recognitions.map((r) =>
            r.id === id
              ? {
                ...r,
                reactions: {
                  ...r.reactions,
                  [reactionKey]: Math.max(0, r.reactions[reactionKey] + delta),
                },
              }
              : r,
          ),
        })),
      addRecognitionComment: (id, comment) =>
        set((state) => ({
          recognitions: state.recognitions.map((r) =>
            r.id === id
              ? { ...r, comments: [...r.comments, comment] }
              : r,
          ),
        })),

      // Leaderboard
      setLeaderboard: (entries, total) => set((state) => ({ leaderboard: entries, leaderboardTotal: total ?? state.leaderboardTotal })),
      setLeaderboardLoading: (loading) => set({ leaderboardLoading: loading }),
      setLeaderboardTab: (tab) => set({ leaderboardTab: tab, leaderboardPage: 1 }),
      setLeaderboardPeriod: (period) => set({ leaderboardPeriod: period, leaderboardPage: 1 }),
      setLeaderboardPage: (page) => set({ leaderboardPage: page }),
      setLeaderboardPageSize: (size) => set({ leaderboardPageSize: size, leaderboardPage: 1 }),

      // Badges
      setBadges: (badges) => set({ badges }),
      setBadgesLoading: (loading) => set({ badgesLoading: loading }),
      setBadgeCategory: (category) => set({ badgeCategory: category }),

      // Rewards
      setRewards: (rewards) => set({ rewards }),
      setRewardsLoading: (loading) => set({ rewardsLoading: loading }),
      setRewardCategory: (category) => set({ rewardCategory: category }),

      // Stats & Dashboard
      setMyStats: (stats) => set({ myStats: stats }),
      setStatsLoading: (loading) => set({ statsLoading: loading }),
      setTrendingValues: (values) => set({ trendingValues: values }),
      setTopRecognized: (employees) => set({ topRecognized: employees }),
      setDashboardLoading: (loading) => set({ dashboardLoading: loading }),

      // Analytics
      setAnalytics: (analytics) => set({ analytics }),
      setAnalyticsLoading: (loading) => set({ analyticsLoading: loading }),

      // Company Values
      setCompanyValues: (values) => set({ companyValues: values }),

      // Feed refresh — increment version to trigger useEffect refetch
      refreshFeed: () => set((state) => ({ feedVersion: state.feedVersion + 1 })),

      // Highlight
      setHighlightedRecognitionId: (id) => set({ highlightedRecognitionId: id }),

      // Dialog
      openGiveDialog: () => set({ isGiveDialogOpen: true }),
      closeGiveDialog: () => set({ isGiveDialogOpen: false }),

      // Error
      setError: (error) => set({ error }),

      // Reset
      reset: () => set(initialState),
    }),
    { name: 'recognition-store' },
  ),
)
